import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Card, CardContent, Typography, Box } from '@mui/material'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import RemoveIcon from '@mui/icons-material/Remove'
import { InfoTooltip } from '../atoms'
import { gsap } from 'gsap'

import styles from './MetricCard.module.scss'

const ComparisonStat = ({ label, current, previous, tooltip }) => {
  if (previous === null || previous === undefined) return null

  if (previous === 0 && current === 0) return null

  const diff = current - previous
  let pctChange
  if (previous === 0) {
    pctChange = 'N/A'
  } else {
    pctChange = ((diff / previous) * 100).toFixed(1)
  }

  const isPositive = diff > 0
  const isNeutral = diff === 0 || previous === 0
  const colorClass = isNeutral
    ? styles.compNeutral
    : isPositive ? styles.compPositive : styles.compNegative

  const ArrowIcon = isNeutral ? RemoveIcon : isPositive ? ArrowUpwardIcon : ArrowDownwardIcon

  const displayPct = previous === 0
    ? 'N/A'
    : `${isPositive ? '+' : ''}${pctChange}%`

  return (
    <Box className={`${styles.compRow} ${colorClass}`}>
      <ArrowIcon className={styles.compArrow} />
      <span className={styles.compPct}>{isNeutral && previous !== 0 ? '0%' : displayPct}</span>
      <span className={styles.compLabel}>{label}</span>
      {tooltip && <InfoTooltip content={tooltip} placement='top' />}
    </Box>
  )
}

ComparisonStat.propTypes = {
  label: PropTypes.string.isRequired,
  current: PropTypes.number,
  previous: PropTypes.number,
  tooltip: PropTypes.string
}

const AnalyticsMetricCardView = ({
  icon: Icon,
  value,
  label,
  description,
  type = 'daily',
  decimals = 0,
  isPercentage = false,
  isRawText = false,
  additionalInfo,
  className,
  prevValue,
  avgValue,
  workdaysLabel
}) => {
  const valueRef = useRef(null)

  /* istanbul ignore next */
  useEffect(() => {
    let animation

    if (!isRawText && valueRef.current && value !== undefined && value !== null && typeof gsap !== 'undefined' && gsap.fromTo) {
      animation = gsap.fromTo(
        valueRef.current,
        { innerText: 0 },
        {
          innerText: parseFloat(value),
          duration: 1.5,
          ease: 'power2.out',
          snap: { innerText: 1 / Math.pow(10, decimals) },
          onUpdate: function () {
            if (valueRef.current) {
              let currentValue = parseFloat(this.targets()[0].innerText)

              if (isNaN(currentValue)) currentValue = 0

              const formatted =
                decimals > 0 ? currentValue.toFixed(decimals) : Math.round(currentValue)

              valueRef.current.innerText = isPercentage ? `${formatted}%` : formatted
            }
          }
        }
      )
    }

    return () => {
      if (animation) {
        animation.kill()
      }
    }
  }, [value, decimals, isPercentage, isRawText])

  const hasComparisons = prevValue !== undefined || avgValue !== undefined
  const numericValue = parseFloat(value) || 0

  return (
    <Card elevation={0} className={`${styles.metricCard} ${styles[type] || ''} ${className || ''} metric-card card-interactive`}>
      <CardContent className={styles.cardContent}>
        {/* Label + tooltip */}
        <Box className={styles.labelWrapper}>
          <Typography variant='subtitle1' component='span' className={styles.metricLabel}>
            {label}
          </Typography>
          {description && (
            <InfoTooltip content={description} placement='top' />
          )}
        </Box>

        {/* Icon + value */}
        <Box className={styles.labelWrapper}>
          {Icon && <Icon className={styles.metricIcon} />}
          <Typography
            variant='h3'
            component='p'
            className={`${styles.metricValue} ${isRawText ? '' : 'metric-value-animate'}`}
            ref={valueRef}
            data-value={value ?? 0}
            data-decimals={decimals}
            data-percentage={isPercentage}
          >
            {isRawText
              ? value || '—'
              : isPercentage
                ? `${decimals > 0 ? (value ?? 0).toFixed(decimals) : (value ?? 0)}%`
                : decimals > 0
                  ? (value ?? 0).toFixed(decimals)
                  : (value ?? 0)}
          </Typography>
        </Box>

        {additionalInfo && <Typography variant='subtitle2'>{additionalInfo}</Typography>}

        {/* Comparison stats */}
        {hasComparisons && (
          <Box className={styles.compSection}>
            {prevValue !== undefined && (
              <ComparisonStat
                label='vs prev day'
                current={numericValue}
                previous={prevValue}
                tooltip={`Compared to last business day (${workdaysLabel || 'Mon\u2013Fri'})`}
              />
            )}
            {avgValue !== undefined && (
              <ComparisonStat
                label='vs 30d avg'
                current={numericValue}
                previous={avgValue}
                tooltip={`Compared to 30-day business day average (${workdaysLabel || 'Mon\u2013Fri'})`}
              />
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

AnalyticsMetricCardView.propTypes = {
  icon: PropTypes.elementType,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  label: PropTypes.string.isRequired,
  description: PropTypes.string,
  type: PropTypes.string,
  decimals: PropTypes.number,
  isPercentage: PropTypes.bool,
  isRawText: PropTypes.bool,
  additionalInfo: PropTypes.node,
  className: PropTypes.string,
  prevValue: PropTypes.number,
  avgValue: PropTypes.number,
  workdaysLabel: PropTypes.string
}

export default AnalyticsMetricCardView
